import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, fmtError } from "../api";
import { Inbox, RefreshCw, ChevronRight, AlertTriangle, Paperclip, User, Clock, ArrowLeft, Info } from "lucide-react";

// Status-Filter – gleiche Benennung/Farben wie im Dashboard.
const STATUS = [
  { key: "open", label: "Offen", color: "#B91C1C", bg: "#FEF2F2" },
  { key: "in_progress", label: "In Arbeit", color: "#1D4ED8", bg: "#EFF6FF" },
  { key: "completed", label: "Erledigt", color: "#15803D", bg: "#F0FDF4" },
  { key: "external_service_required", label: "Fachfirma", color: "#B45309", bg: "#FFFBEB" },
];
const statusMeta = (k) => STATUS.find((s) => s.key === k);

const MISSING_LABELS = {
  description: "Meldungstext / Beschreibung",
  reporterEmail: "E-Mail der meldenden Stelle",
  status: "Workflow-Status (Neu / Angenommen / Erledigt)",
  assignedToName: "Bearbeiter / zuständige Person",
  title: "Aussagekräftiger Titel",
};

function fmtTime(iso) {
  try {
    return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" }).format(new Date(iso));
  } catch {
    return iso || "";
  }
}

export default function Meldungen() {
  const { permid } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = sessionStorage.getItem("mag_token");

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!token) { navigate("/login"); return; }
    setLoading(true); setError("");
    api.get(`/magauth/messages/${permid}`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { include_children: 1 },
    })
      .then(({ data }) => setData(data))
      .catch((e) => { setError(fmtError(e, "Meldungen konnten nicht geladen werden")); setData(null); })
      .finally(() => setLoading(false));
  }, [permid, token, navigate]);

  useEffect(() => { load(); }, [load]);

  const messages = useMemo(() => data?.messages || [], [data]);
  const missingUnion = Array.from(new Set(messages.flatMap((m) => m.missingFields || [])));
  const activeStatus = searchParams.get("status") || "";
  const statusCounts = useMemo(() => {
    const c = {};
    for (const m of messages) c[m.status] = (c[m.status] || 0) + 1;
    return c;
  }, [messages]);
  const filtered = activeStatus ? messages.filter((m) => m.status === activeStatus) : messages;
  const setStatus = (k) => { if (k) setSearchParams({ status: k }); else setSearchParams({}); };

  const open = (m) => navigate(`/vorgang/${m.id}?permid=${m.sourcePermId || permid}`, { state: { issue: m } });

  return (
    <div className="container-wd py-8" data-testid="meldungen-page">
      <button className="btn-secondary mb-4" data-testid="meldungen-back" onClick={() => navigate("/objekte")}
        style={{ padding: "6px 12px", fontSize: 13 }}>
        <ArrowLeft size={14} /> Zurück zu den Objekten
      </button>

      <div className="flex justify-between items-start mb-4 flex-wrap gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-extrabold flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#FFF7ED" }}>
              <Inbox size={18} color="#F97316" />
            </span>
            Eingegangene Meldungen
          </h1>
          {data && (
            <p className="text-sm mt-1" style={{ color: "#64748B" }} data-testid="meldungen-source">
              Objekt <code>{permid}</code> + Standorte · {data.count} Meldungen aus {data.source_count} Objekten · davon {data.mappable_count} als Vorgang darstellbar · via wdtransfer
            </p>
          )}
        </div>
        <button className="btn-secondary" data-testid="meldungen-refresh" onClick={load} disabled={loading}>
          <RefreshCw size={14} /> Aktualisieren
        </button>
      </div>

      {error && (
        <div className="card-wd p-5 mb-4" style={{ borderColor: "#DC2626" }} data-testid="meldungen-error">
          <p className="text-sm font-semibold m-0" style={{ color: "#DC2626" }}>{error}</p>
        </div>
      )}

      {loading && !data && (
        <p className="text-sm py-2" style={{ color: "#64748B" }} data-testid="meldungen-loading">Meldungen werden geladen…</p>
      )}

      {data && messages.length === 0 && !error && (
        <div className="card-wd p-8 text-center" data-testid="meldungen-empty">
          <Inbox size={28} className="mx-auto mb-2" style={{ color: "#94A3B8" }} />
          <p className="font-bold mb-1">Keine Meldungen vorhanden</p>
          <p className="text-sm" style={{ color: "#64748B" }}>Für dieses Objekt liegen aktuell keine Meldungen vor.</p>
        </div>
      )}

      {data && missingUnion.length > 0 && (
        <div className="card-wd p-4 mb-4" style={{ background: "#FFFBEB", border: "1px solid #FDE68A" }} data-testid="meldungen-missing">
          <div className="flex items-center gap-2 font-bold text-sm mb-1" style={{ color: "#92400E" }}>
            <Info size={16} aria-hidden="true" /> Hinweis: Im Meldeformat fehlen für den vollständigen Vorgang folgende Felder
          </div>
          <ul className="text-sm m-0 pl-5" style={{ color: "#92400E", listStyle: "disc" }}>
            {missingUnion.map((f) => <li key={f} data-testid={`meldungen-missing-${f}`}>{MISSING_LABELS[f] || f}</li>)}
          </ul>
        </div>
      )}

      {data && messages.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-4" data-testid="meldungen-status-filter">
          <button data-testid="meldungen-filter-all" onClick={() => setStatus("")}
            className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-3 py-1.5"
            style={{ border: `1px solid ${!activeStatus ? "#0F172A" : "#E2E8F0"}`, background: !activeStatus ? "#0F172A" : "#fff", color: !activeStatus ? "#fff" : "#64748B", cursor: "pointer" }}>
            Alle <span className="tabular-nums" style={{ opacity: 0.85 }}>{messages.length}</span>
          </button>
          {STATUS.map((s) => {
            const active = activeStatus === s.key;
            return (
              <button key={s.key} data-testid={`meldungen-filter-${s.key}`} onClick={() => setStatus(s.key)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full px-3 py-1.5"
                style={{ border: `1px solid ${active ? s.color : "#E2E8F0"}`, background: active ? s.bg : "#fff", color: active ? s.color : "#64748B", cursor: "pointer" }}>
                <span className="inline-block rounded-full" style={{ width: 7, height: 7, background: s.color }} />
                {s.label} <span className="tabular-nums" style={{ opacity: 0.85 }}>{statusCounts[s.key] || 0}</span>
              </button>
            );
          })}
        </div>
      )}

      {data && messages.length > 0 && filtered.length === 0 && (
        <div className="card-wd p-6 text-center" data-testid="meldungen-filter-empty">
          <p className="text-sm m-0" style={{ color: "#64748B" }}>Keine Meldungen mit dem Status „{statusMeta(activeStatus)?.label || activeStatus}".</p>
        </div>
      )}

      <div className="flex flex-col gap-3">
        {filtered.map((m) => (
          <button key={m.id} type="button" onClick={() => open(m)} data-testid={`meldung-item-${m.id}`}
            className="card-wd p-4 text-left w-full"
            style={{ cursor: "pointer", border: "1px solid #E2E8F0", transition: "border-color .15s ease, box-shadow .15s ease" }}>
            <div className="flex justify-between items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="text-xs font-bold" style={{ color: "#94A3B8" }}>{m.referenceNumber}</span>
                  {statusMeta(m.status) && (
                    <span data-testid={`meldung-status-${m.id}`} className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-md"
                      style={{ background: statusMeta(m.status).bg, color: statusMeta(m.status).color }}>
                      <span className="inline-block rounded-full" style={{ width: 6, height: 6, background: statusMeta(m.status).color }} /> {statusMeta(m.status).label}
                    </span>
                  )}
                  {m.sourceTitle && (
                    <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-md"
                      style={{ background: "#EFF6FF", color: "#1D4ED8" }} data-testid={`meldung-source-${m.id}`}>
                      {m.sourceTitle}
                    </span>
                  )}
                  {!m.mappable && (
                    <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-md"
                      style={{ background: "#FEF2F2", color: "#B91C1C" }} data-testid={`meldung-unmappable-${m.id}`}>
                      <AlertTriangle size={12} /> unvollständig
                    </span>
                  )}
                  {m.hasAttachments && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md" style={{ background: "#F1F5F9", color: "#475569" }}>
                      <Paperclip size={12} /> Anhang
                    </span>
                  )}
                </div>
                <div className="font-bold text-sm truncate">{m.title}</div>
                <div className="flex items-center gap-4 mt-1 text-xs flex-wrap" style={{ color: "#64748B" }}>
                  <span className="inline-flex items-center gap-1"><User size={12} /> {m.reporterName}</span>
                  <span className="inline-flex items-center gap-1"><Clock size={12} /> {fmtTime(m.reportedAt)}</span>
                </div>
              </div>
              <span className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#F1F5F9" }}>
                <ChevronRight size={16} color="#64748B" />
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
